import { useCallback, useState } from "react";
import type {
  ActiveNode,
  Agent,
  App,
  Component
} from "../types/platform";

export const LAYER_RADII = {
  agent: 20,
  app: 38,
  b2proc: 46
} as const;

export const APP_ANGLES: Readonly<Record<string, number>> = {
  ILS: 0,
  Marketplace: 40,
  Coach: 75,
  CSR: 105,
  Ops: 130,
  Atlas: 195,
  Skyfall: 220,
  Enigma: 245,
  Conduit: 270,
  Simon: 305,
  ECP: 335
};

export const B2PROC_ANGLE = 180;

export function useActiveNode() {
  const [hovered, setHovered] = useState<ActiveNode>(null);
  const [pinned, setPinned] = useState<ActiveNode>(null);

  const hoverAgent = useCallback(
    (agent: Agent) => setHovered({ kind: "agent", data: agent }),
    []
  );
  const hoverApp = useCallback(
    (app: App) => setHovered({ kind: "app", data: app }),
    []
  );
  const hoverComponent = useCallback(
    (component: Component) =>
      setHovered({ kind: "component", data: component }),
    []
  );
  const clearHover = useCallback(() => setHovered(null), []);

  const togglePinAgent = useCallback(
    (agent: Agent) =>
      setPinned((prev) =>
        prev?.kind === "agent" && prev.data.name === agent.name
          ? null
          : { kind: "agent", data: agent }
      ),
    []
  );
  const togglePinApp = useCallback(
    (app: App) =>
      setPinned((prev) =>
        prev?.kind === "app" && prev.data.name === app.name
          ? null
          : { kind: "app", data: app }
      ),
    []
  );
  const togglePinComponent = useCallback(
    (component: Component) =>
      setPinned((prev) =>
        prev?.kind === "component" && prev.data.name === component.name
          ? null
          : { kind: "component", data: component }
      ),
    []
  );
  const clearPin = useCallback(() => setPinned(null), []);

  return {
    active: pinned ?? hovered,
    pinned,
    hoverAgent,
    hoverApp,
    hoverComponent,
    clearHover,
    togglePinAgent,
    togglePinApp,
    togglePinComponent,
    clearPin
  };
}

export function posOnCircle(
  i: number,
  n: number,
  radius: number
): { x: number; y: number } {
  const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
  return {
    x: 50 + radius * Math.cos(angle),
    y: 50 + radius * Math.sin(angle)
  };
}

export function posAtAngle(
  angleDegrees: number,
  radius: number
): { x: number; y: number } {
  const angle = (angleDegrees * Math.PI) / 180 - Math.PI / 2;
  return {
    x: 50 + radius * Math.cos(angle),
    y: 50 + radius * Math.sin(angle)
  };
}
